import * as React from 'react'
import { ReactNode } from 'react'
import { Loader } from '@/ui'
import { AccessConfig, useHasAccess } from '@/modules/shared/permissions/access'
import { NoPermissionScreen } from './NoPermissionScreen'

interface PermissionGuardProps extends AccessConfig {
  children: ReactNode
  /** Company to scope the check to. Defaults to the `:companyId` route param. */
  companyId?: string
  /** Rendered when access is denied. Defaults to <NoPermissionScreen />. */
  fallback?: ReactNode
  /** Rendered while account/roles are loading. Defaults to a centred loader. */
  loadingFallback?: ReactNode
  /** Visual variant for the default no-permission screen. */
  fallbackVariant?: 'page' | 'inline'
}

/**
 * Renders `children` only when the current user satisfies the access rule;
 * otherwise shows the no-permission screen (or a custom `fallback`). While auth
 * data loads it shows a loader so the screen never flashes as denied.
 *
 * ```tsx
 * // declarative: any of the flags
 * <PermissionGuard permissions={['canViewBillingData', 'canEditBillingData']}>
 *   <BillingSection />
 * </PermissionGuard>
 *
 * // custom rule via predicate
 * <PermissionGuard require={homeAccess.creator}>
 *   <CreatorPage />
 * </PermissionGuard>
 * ```
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  companyId,
  fallback,
  loadingFallback,
  fallbackVariant = 'page',
  ...config
}) => {
  const { allowed, isLoading } = useHasAccess(config, companyId)

  if (isLoading) {
    return <>{loadingFallback ?? <Loader fill />}</>
  }

  if (!allowed) {
    return <>{fallback ?? <NoPermissionScreen variant={fallbackVariant} />}</>
  }

  return <>{children}</>
}
